// Revisiones — the owner's one screen for every human decision in Manos de Oro (MDO-6, D13).
// v3 (OB-P1, Oct 14 2026, owner): each job card says what it is (one job, N photos), why it's here, a suggestion, and what
//   Approve will do; standards in English (MDO's own text) with the AI's Spanish note; an Operator tips shelf.
// v2 (OB-P1, Oct 14 2026): English (the Ops console is English; notes that go to a Maestro are still written in Spanish, and the
//   interview questions keep their Spanish wording); a failed load shows its reason — never "Nothing pending" over an error.
// Talks only to GM's ops-mdo proxy (which checks the operator role). Viewers can look; only the owner can act —
// the server enforces it, this screen just shows the answer.
import { useCallback, useEffect, useState } from "react";
import { OFICIO } from "./brand";   // v3: trade names in English
import type { OpsClient, Tab } from "./api";

const TABS: { key: Tab; label: string }[] = [
  { key: "obras", label: "Jobs" }, { key: "visto_bueno", label: "Maestro interviews" }, { key: "apelaciones", label: "Appeals" },
  { key: "senales", label: "Signals" }, { key: "fraude", label: "Fraud" },
];
const CAL_SET_DEFAULT = "calibracion-1";
const S = {
  wrap: { fontFamily: "system-ui, sans-serif", padding: 16, display: "grid", gap: 12 } as React.CSSProperties,
  card: { background: "#fff", border: "1px solid #e5e5e5", borderRadius: 12, padding: 14, display: "grid", gap: 10 } as React.CSSProperties,
  btn: { padding: "8px 12px", borderRadius: 8, border: "1px solid #2B231C", background: "#fff", color: "#2B231C", fontWeight: 600, cursor: "pointer" } as React.CSSProperties,
  ok: { padding: "8px 12px", borderRadius: 8, border: "none", background: "#2B231C", color: "#fff", fontWeight: 600, cursor: "pointer" } as React.CSSProperties,
  warn: { padding: "8px 12px", borderRadius: 8, border: "none", background: "#8F1D2C", color: "#fff", fontWeight: 600, cursor: "pointer" } as React.CSSProperties,
};

function Photos({ urls }: { urls?: string[] }) {
  if (!urls?.length) return <em style={{ color: "#888" }}>No photos</em>;
  return (
    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(140px, 1fr))", gap: 6 }}>
      {urls.filter(Boolean).map((u, i) => <a key={i} href={u} target="_blank" rel="noreferrer"><img src={u} alt="" style={{ width: "100%", aspectRatio: "4/3", objectFit: "cover", borderRadius: 8 }} /></a>)}
    </div>
  );
}

function tradeName(k?: string) { return (k && (OFICIO as Record<string, { en: string }>)[k]?.en) || k || "?"; }
function who(c: Record<string, any>) { const p = c.person ?? {}; return p.display_name || p.phone || "Maestro (no name yet)"; }

function Facts({ c }: { c: Record<string, any> }) {
  const p = c.person ?? {};
  return (
    <div style={{ display: "flex", flexWrap: "wrap", gap: 12, fontSize: 14, color: "#333" }}>
      <span><b>{tradeName(c.oficio_key)}</b> · {c.work_date ?? "no date"} · {c.source === "partner_job" ? "Green Maestro job" : "from his phone"}</span>
      <span>{who(c)} ({p.approved_in_oficio ?? 0} approved in this trade)</span>
      {c.risk_band && <span style={{ color: c.risk_band === "red" ? "#8F1D2C" : c.risk_band === "yellow" ? "#8a6d00" : "#2B231C" }}>risk {c.risk_band} ({c.risk_score ?? 0})</span>}
      {c.is_test && <span style={{ color: "#888" }}>TEST</span>}
    </div>
  );
}

// v3 (OB-P1, Oct 14 2026, owner): a job card says what it is, why it's here, a suggestion, and what Approve will do.
const REASON: Record<string, string> = {
  ai_unsure: "The AI couldn't decide.",
  same_job_false: "The photos may not all be from the same job.",
  low_confidence: "The AI wasn't confident.",
  must_failed: "A must-have standard looks missed.",
};
const FLAG: Record<string, string> = {
  no_metadata: "Photos have no camera date — they may be screenshots or downloads.",
  no_series: "Photos weren't taken as a set.",
  web_match: "A photo matches one found on the internet.",
  duplicate: "A photo was already used in another job.",
  edited: "A photo looks edited.",
};
const TITLE_AT = (n: number) => (n >= 10 ? "Maestro" : n >= 3 ? "Oficial" : n >= 1 ? "Aprendiz" : null);

function JobSummary({ c }: { c: Record<string, any> }) {
  const items: any[] = c.items ?? [];
  const signals: any[] = c.signals ?? [];
  const nPhotos = (c.photos ?? []).filter(Boolean).length;
  const met = items.filter((i) => i.result === "pass").length;
  const missed = items.filter((i) => i.result === "fail").length;
  const unknown = items.length - met - missed;
  const mustMissed = items.some((i) => i.must && i.result === "fail");
  const weak = c.queue_reason === "same_job_false" || signals.some((x) => ["no_metadata", "no_series", "web_match", "duplicate", "edited"].includes(x.id));
  const suggestion =
    weak ? { t: "Weak evidence — usually Not yet: ask for the original phone photos of one job.", tone: "#8a6d00" }
    : items.length && unknown >= Math.ceil(items.length * 0.6) ? { t: "The photos can't show the finished work — usually Not yet.", tone: "#8a6d00" }
    : mustMissed ? { t: "A must-have standard is missed — usually Not yet.", tone: "#8a6d00" }
    : items.length && items.filter((i) => i.must).every((i) => i.result === "pass") && met >= Math.ceil(items.length / 2)
      ? { t: "Every must-have met and most standards met — usually Approve.", tone: "#2f6b3a" }
    : { t: "Your call — look at the photos and the standards below.", tone: "#2B231C" };
  const have = c.person?.approved_in_oficio ?? 0;
  const before = TITLE_AT(have), after = TITLE_AT(have + 1);
  return (
    <div style={{ display: "grid", gap: 6, fontSize: 15 }}>
      <div style={{ fontSize: 17, fontWeight: 700 }}>{who(c)} · {tradeName(c.oficio_key)}</div>
      <div style={{ color: "#444" }}>
        1 job · {nPhotos} photo{nPhotos === 1 ? "" : "s"} · {c.work_date ? `done ${c.work_date}` : "no date"} · {c.source === "partner_job" ? "a Green Maestro job (strong evidence)" : "from his phone"}
        {c.is_test && <span style={{ color: "#888" }}> · TEST</span>}
      </div>
      <div style={{ background: "#F3EFE6", borderRadius: 8, padding: "8px 10px" }}>
        These {nPhotos} photo{nPhotos === 1 ? " is" : "s are"} from <b>one job</b>. <b>Approve</b> or <b>Not yet</b> decides the whole job.
      </div>
      {c.queue_reason && <div><b>Why it's here:</b> {REASON[c.queue_reason] ?? c.queue_reason}</div>}
      {items.length > 0 && <div><b>Standards:</b> {met} met · {missed} missed · {unknown} can't tell{mustMissed ? " — a must-have is missed" : ""}</div>}
      {signals.map((x) => <div key={x.id} style={{ color: "#8a6d00" }}>⚑ {FLAG[x.id] ?? x.id}</div>)}
      <div style={{ color: suggestion.tone, fontWeight: 600 }}>Suggestion: {suggestion.t}</div>
      <div><b>If you approve:</b> {have + 1} approved {tradeName(c.oficio_key)} job{have + 1 === 1 ? "" : "s"}{after ? ` → ${after}` : ""}{after && after !== before ? " (new título)" : ""}. His photos turn ✓ Verified on his page.</div>
    </div>
  );
}

function Items({ c }: { c: Record<string, any> }) {
  const items: any[] = c.items ?? [];
  const signals: any[] = c.signals ?? [];
  return (
    <div style={{ fontSize: 14, display: "grid", gap: 4 }}>
      {items.map((i) => (
        <div key={i.id}>
          {i.result === "pass" ? "✅" : i.result === "fail" ? "❌" : "◻️"} <b>{i.text_en ?? i.id}</b>{i.must ? <span style={{ color: "#8F1D2C" }}> · must-have</span> : null}
          {i.note_es && <div style={{ color: "#666", fontStyle: "italic", marginLeft: 22 }}>AI note (Spanish): {i.note_es}</div>}
        </div>
      ))}
      {signals.map((x) => <div key={x.id} style={{ color: "#8a6d00" }}>⚑ {FLAG[x.id] ?? x.id} (+{x.weight})</div>)}
      {c.tags?.text_in_photo?.length > 0 && <div>Text in photos: {c.tags.text_in_photo.join(", ")}</div>}
    </div>
  );
}

export function Revisiones({ client }: { client: OpsClient }) {
  const [cal, setCal] = useState(false);
  if (cal) return (
    <div style={S.wrap}>
      <button style={S.btn} onClick={() => setCal(false)}>← Reviews</button>
      <Calibracion client={client} />
    </div>
  );
  return <RevisionesTabs client={client} onCal={() => setCal(true)} />;
}

function RevisionesTabs({ client, onCal }: { client: OpsClient; onCal: () => void }) {
  const [tab, setTab] = useState<Tab>("obras");
  const [counts, setCounts] = useState<Record<string, number>>({});
  const [items, setItems] = useState<Record<string, any>[]>([]);
  const [sel, setSel] = useState<string[]>([]);
  const [msg, setMsg] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);   // OB-P1: a failed load is never shown as "nothing pending"

  const load = useCallback(async () => {
    setBusy(true);
    try { const [c, q] = await Promise.all([client.counts(), client.queue(tab)]); setCounts(c); setItems(q.items); setSel([]); setLoadError(null); }
    catch (e) {
      const m = (e as Error).message;
      setLoadError(m === "not_an_operator" ? "This sign-in isn't an Ops operator, so the review queue can't load. Sign out and sign in with an operator account."
        : m === "not_authorized" ? "This account isn't allowed to see reviews." : `Couldn't load the review queue (${m}). Tap Refresh.`);
    }
    finally { setBusy(false); }
  }, [client, tab]);
  useEffect(() => { load(); }, [load]);

  const act = async (fn: () => Promise<unknown>, done = "Done") => {
    setMsg(null);
    try { await fn(); setMsg(done); await load(); }
    catch (e) { setMsg((e as Error).message === "owner_only" ? "Only the owner can decide." : (e as Error).message); }
  };
  const ask = (q: string) => (window.prompt(q) ?? "").trim();

  const selPeople = new Set(items.filter((c) => sel.includes(c.obra_id)).map((c) => `${c.person?.person_id}|${c.oficio_key}`));
  return (
    <div style={S.wrap}>
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
        {TABS.map((x) => (
          <button key={x.key} onClick={() => setTab(x.key)} style={tab === x.key ? S.ok : S.btn}>
            {x.label} ({counts[x.key] ?? 0}{counts[`${x.key}_overdue`] ? ` · ${counts[`${x.key}_overdue`]} overdue` : ""})
          </button>
        ))}
        <button style={S.btn} onClick={load}>{busy ? "…" : "Refresh"}</button>
        <button style={S.btn} onClick={onCal}>Calibration</button>
      </div>
      {tab === "obras" && (
        <details style={{ ...S.card, background: "#F7F4EC" }}>
          <summary style={{ cursor: "pointer", fontWeight: 700 }}>Operator tips</summary>
          <ol style={{ margin: "6px 0 0", paddingLeft: 20, display: "grid", gap: 6, fontSize: 14 }}>
            <li><b>Each card is one job.</b> All its photos belong to that job; Approve or Not yet decides the whole job, never a single photo.</li>
            <li><b>Approve</b> when the photos show <i>finished</i> work that meets the must-have standards. Small site-cleanup misses (weeds, a tarp) are fine.</li>
            <li><b>Not yet</b> when the work isn't finished, the photos can't show the quality, or a must-have is missed. Write <b>one</b> fix <b>in Spanish</b> — it's sent to him.</li>
            <li><b>Watch the ⚑ flags.</b> No camera date, not a set, or "may be different jobs" means weak evidence: ask for the original phone photos of one job.</li>
            <li><b>Approvals can't be undone</b> (a título is only revoked for fraud). Green Maestro jobs are the strongest evidence; use "Maestro fundador" only for someone you personally know is a master.</li>
          </ol>
        </details>
      )}
      {msg && <div style={{ ...S.card, background: "#fffbe6" }}>{msg}</div>}

      {tab === "obras" && sel.length > 0 && (
        <div style={{ ...S.card, position: "sticky", top: 0, zIndex: 1 }}>
          {selPeople.size === 1
            ? <button style={S.ok} onClick={() => act(() => client.batchApprove(sel), `${sel.length} approved`)}>Approve {sel.length} selected</button>
            : <span>Batch approval works for one person in one trade.</span>}
        </div>
      )}

      {loadError && <div style={{ ...S.card, background: "#FBEDEA", color: "#8F1D2C", fontWeight: 600 }}>{loadError}</div>}
      {items.length === 0 && !busy && !loadError && <div style={{ color: "#666" }}>Nothing pending.</div>}

      {items.map((c) => {
        const key = c.obra_id ?? c.review_id;
        if (tab === "obras") return (
          <div key={key} style={S.card}>
            <label style={{ display: "flex", gap: 8, alignItems: "center" }}>
              {c.risk_band !== "red" && <input type="checkbox" checked={sel.includes(c.obra_id)}
                onChange={(e) => setSel(e.target.checked ? [...sel, c.obra_id] : sel.filter((x) => x !== c.obra_id))} />}
              <span style={{ fontSize: 13, color: "#666" }}>select for batch approval</span>
            </label>
            <JobSummary c={c} />
            <Photos urls={c.photos} />
            <details><summary style={{ cursor: "pointer", fontWeight: 600 }}>Standards checked ({(c.items ?? []).length})</summary><Items c={c} /></details>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              <button style={S.ok} onClick={() => act(() => client.decideObra(c.obra_id, "aprobada"))}>Approve</button>
              <button style={S.btn} onClick={() => { const f = ask("One thing to fix — write it in Spanish (it goes to the Maestro):"); if (f) act(() => client.decideObra(c.obra_id, "todavia_no", f)); }}>Not yet</button>
              {c.person?.person_id && (c.person?.approved_in_oficio ?? 0) >= 3 && (
                <button style={S.btn} onClick={() => { if (window.confirm(`Grant "Maestro fundador" in ${tradeName(c.oficio_key)}? This gives the top título outright — only for someone you personally know is a master.`)) act(() => client.foundingGrant(c.person.person_id, c.oficio_key), "Maestro fundador granted"); }}>Maestro fundador</button>
              )}
            </div>
          </div>
        );

        if (tab === "visto_bueno") return <VistoBueno key={key} c={c} client={client} act={act} />;

        if (tab === "apelaciones") return (
          <div key={key} style={S.card}>
            <b>Appeal {c.overdue && <span style={{ color: "#8F1D2C" }}>· overdue</span>}</b>
            {c.obra && (<><Facts c={c.obra} /><Photos urls={c.obra.photos} /><Items c={c.obra} /></>)}
            <div style={{ display: "flex", gap: 8 }}>
              <button style={S.ok} onClick={() => act(() => client.decideReview(c.review_id, "aprobada"))}>Approved</button>
              <button style={S.btn} onClick={() => { const f = ask("One thing to fix — write it in Spanish (it goes to the Maestro):"); if (f) act(() => client.decideReview(c.review_id, "todavia_no", undefined, f)); }}>Not yet</button>
            </div>
          </div>
        );

        if (tab === "senales") return (
          <div key={key} style={S.card}>
            <Facts c={c} /><Photos urls={c.photos} /><Items c={c} />
            {c.checks?.web_match?.results?.some((r: any) => r.examples?.length) && (
              <div style={{ fontSize: 13 }}>En internet: {c.checks.web_match.results.flatMap((r: any) => r.examples ?? []).slice(0, 3).map((u: string) => <a key={u} href={u} target="_blank" rel="noreferrer" style={{ marginRight: 8 }}>{new URL(u).hostname}</a>)}</div>
            )}
            <div style={{ display: "flex", gap: 8 }}>
              <button style={S.btn} onClick={() => { const r = ask("Why is it fine? (saved)"); if (r) act(() => client.resolveFlag(c.obra_id, "dismiss", r), "Signal dismissed; the job is reviewed normally"); }}>It's fine</button>
              <button style={S.warn} onClick={() => { const r = ask("What looks like fraud?"); if (r) act(() => client.resolveFlag(c.obra_id, "escalate", r), "Abierto como caso de fraude"); }}>Posible fraude</button>
            </div>
          </div>
        );

        return <Fraude key={key} c={c} client={client} act={act} />;
      })}
    </div>
  );
}

function VistoBueno({ c, client, act }: { c: Record<string, any>; client: OpsClient; act: (fn: () => Promise<unknown>, d?: string) => Promise<void> }) {
  const [marks, setMarks] = useState<Record<string, string>>({ sabe_explicar: "", sabe_adaptar: "", sabe_cuando_parar: "" });
  const [fix, setFix] = useState("");
  const maestra = (c.approved_obras ?? []).find((o: any) => o.is_obra_maestra);
  const why: string[] = [...(c.buried_items ?? []).map((b: any) => b.text_es), ...((maestra?.talk_topics ?? []).map((t: any) => t.text_es))].slice(0, 3);
  const all = Object.values(marks).every((v) => v === "si");
  const anyNo = Object.values(marks).some((v) => v === "no");
  return (
    <div style={S.card}>
      <b>{c.person?.display_name ?? c.person?.phone} · {c.oficio_key} {c.overdue && <span style={{ color: "#8F1D2C" }}>· call now (7 days)</span>}</b>
      {c.person?.phone && c.marks?.ok_to_call && <a href={`tel:${c.person.phone}`} style={S.ok}>Llamar {c.person.phone}</a>}
      {maestra && (<><div>Masterpiece job:</div><Photos urls={maestra.photos} /></>)}
      <details><summary>Sus {c.approved_obras?.length ?? 0} obras aprobadas</summary>
        {(c.approved_obras ?? []).map((o: any) => <div key={o.obra_id} style={{ marginTop: 8 }}><small>{o.work_date}</small><Photos urls={o.photos} /></div>)}
      </details>
      <ol style={{ margin: 0, paddingLeft: 18, display: "grid", gap: 4 }}>
        <li><b>Tell me about the job.</b> <i>«Cuéntame la obra.»</i></li>
        <li><b>Why this way?</b> <i>«¿Por qué así?»</i>{why.map((w, i) => <div key={i}>— {w}</div>)}</li>
        <li><b>What would you change?</b> <i>«¿Qué cambiarías?»</i></li>
        <li><b>The scenario:</b> show him another site and ask what he would do. <i>«¿Qué harías aquí?»</i></li>
        <li><b>When not?</b> When would he stop the work and call? <i>«¿Cuándo pararías el trabajo y avisarías?»</i></li>
      </ol>
      {([["sabe_explicar", "Can explain"], ["sabe_adaptar", "Can adapt"], ["sabe_cuando_parar", "Knows when to stop"]] as const).map(([k, label]) => (
        <div key={k} style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <span style={{ width: 150 }}>{label}</span>
          <button style={marks[k] === "si" ? S.ok : S.btn} onClick={() => setMarks({ ...marks, [k]: "si" })}>Yes</button>
          <button style={marks[k] === "no" ? S.warn : S.btn} onClick={() => setMarks({ ...marks, [k]: "no" })}>Not yet</button>
        </div>
      ))}
      {anyNo && <input placeholder="One thing to work on — in Spanish (required)" value={fix} onChange={(e) => setFix(e.target.value)} style={{ padding: 8 }} />}
      <button style={S.ok} disabled={!all && !(anyNo && fix.trim())}
        onClick={() => act(() => client.decideReview(c.review_id, all ? "aprobada" : "todavia_no", marks, all ? undefined : fix.trim()),
                           all ? "Maestro granted" : "Saved; he will be told what to work on")}>
        {all ? "Grant Maestro" : "Save as Not yet"}
      </button>
    </div>
  );
}

function Fraude({ c, client, act }: { c: Record<string, any>; client: OpsClient; act: (fn: () => Promise<unknown>, d?: string) => Promise<void> }) {
  const [proposal, setProposal] = useState<{ id: string; at: number } | null>(null);
  const [now, setNow] = useState(Date.now());
  useEffect(() => { if (!proposal) return; const i = setInterval(() => setNow(Date.now()), 1000); return () => clearInterval(i); }, [proposal]);
  const wait = proposal ? Math.max(0, 60 - Math.floor((now - proposal.at) / 1000)) : 0;
  return (
    <div style={{ ...S.card, borderColor: "#8F1D2C" }}>
      <Facts c={c} /><Photos urls={c.photos} /><Items c={c} />
      {!proposal && (
        <button style={S.warn} onClick={async () => {
          const r = (window.prompt("Reason for revoking (saved):") ?? "").trim(); if (!r) return;
          try { const p = await client.revokePropose(c.obra_id, r); setProposal({ id: p.proposal_id, at: Date.now() }); } catch (e) { alert((e as Error).message); }
        }}>Propose revoking</button>
      )}
      {proposal && (
        <div style={{ display: "grid", gap: 8 }}>
          <b>Revisa la evidencia otra vez. Esto quita su título en {c.oficio_key}.</b>
          <button style={S.warn} disabled={wait > 0} onClick={() => act(() => client.revokeConfirm(proposal.id), "Título revoked")}>
            {wait > 0 ? `Confirm in ${wait}s` : "Confirm revocation"}
          </button>
        </div>
      )}
    </div>
  );
}

// MDO-10 §1.1 — blind grading: one obra per screen, big photos, two buttons, a fix field. The grader never sees the
// AI result or anyone else's grade. The report (owner) shows the go-live bar.
function Calibracion({ client }: { client: OpsClient }) {
  const [set, setSet] = useState(CAL_SET_DEFAULT);
  const [cur, setCur] = useState<{ progress: { graded: number; total: number }; obra: Record<string, any> | null } | null>(null);
  const [fix, setFix] = useState("");
  const [report, setReport] = useState<Record<string, any> | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  const next = useCallback(async () => { setFix(""); try { setCur(await client.calNext(set)); } catch (e) { setMsg((e as Error).message); } }, [client, set]);
  useEffect(() => { next(); }, [next]);
  const grade = async (outcome: "aprobada" | "todavia_no") => {
    if (!cur?.obra) return;
    if (outcome === "todavia_no" && !fix.trim()) { setMsg("Write one thing to fix (in Spanish)."); return; }
    setMsg(null);
    try { await client.calGrade(set, cur.obra.obra_id, outcome, outcome === "todavia_no" ? fix.trim() : undefined); await next(); }
    catch (e) { setMsg((e as Error).message); }
  };
  return (
    <div style={{ display: "grid", gap: 12 }}>
      <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
        <b>Calibration</b>
        <input value={set} onChange={(e) => setSet(e.target.value)} style={{ padding: 6 }} />
        {cur && <span>{cur.progress.graded} / {cur.progress.total}</span>}
        <button style={S.btn} onClick={async () => setReport(await client.calReport(set))}>Report</button>
        <button style={S.btn} onClick={async () => { try { const r = await client.calRun(set); setMsg(`AI: ${r.ran} reviewed, ${r.failed} failed`); } catch (e) { setMsg((e as Error).message); } }}>Run the AI (calibration)</button>
      </div>
      {msg && <div style={{ ...S.card, background: "#fffbe6" }}>{msg}</div>}
      {report && (
        <div style={{ ...S.card, borderColor: report.bar_met ? "#2B231C" : "#8F1D2C" }}>
          <b>{report.bar_met ? "✅ The bar to go live is met" : "❌ The bar to go live is not met yet"}</b>
          <div>AI vs majority agreement: <b>{report.agreement_pct ?? "—"}%</b> (target ≥ 85%) · AI approved something everyone rejected: <b>{report.ai_approved_all_rejected}</b> (meta 0)</div>
          <div>Kappa de Cohen: {report.cohen_kappa ?? "—"} · Kappa de Fleiss: {report.fleiss_kappa ?? "—"} · IA indecisa: {report.ai_undecided} · Empates: {report.ties}</div>
          <div>Planted fakes caught (yellow/red): {report.planted_fakes_ok ? "✅ all" : "❌ not all"}</div>
          <details><summary>Desacuerdos ({report.disagreements?.length ?? 0})</summary>
            {(report.disagreements ?? []).map((d: any) => (
              <div key={d.obra_id} style={{ fontSize: 13, borderTop: "1px solid #eee", padding: "6px 0" }}>
                {d.obra_id.slice(0, 8)} · AI: <b>{d.ai}</b> · majority: <b>{d.majority}</b>
                {(d.grades ?? []).map((g: any) => <div key={g.grader}>— {g.grader}: {g.outcome}{g.fix ? ` (${g.fix})` : ""}</div>)}
              </div>
            ))}
          </details>
        </div>
      )}
      {cur && !cur.obra && <div style={S.card}>You've graded every job in this set. Thank you!</div>}
      {cur?.obra && (
        <div style={S.card}>
          <div style={{ fontSize: 14, color: "#555" }}>{cur.obra.oficio_key} · {cur.obra.work_date ?? "sin fecha"}</div>
          <Photos urls={cur.obra.photos} />
          <input placeholder="If Not yet: one thing to fix — in Spanish" value={fix} onChange={(e) => setFix(e.target.value)} style={{ padding: 10, fontSize: 16 }} />
          <div style={{ display: "flex", gap: 10 }}>
            <button style={{ ...S.ok, fontSize: 18, padding: "12px 20px" }} onClick={() => grade("aprobada")}>Approved</button>
            <button style={{ ...S.warn, fontSize: 18, padding: "12px 20px" }} onClick={() => grade("todavia_no")}>Not yet</button>
          </div>
        </div>
      )}
    </div>
  );
}
